<main
  id="main-content"
  [attr.aria-label]="module?.name || 'Module'"
  [attr.inert]="isNotebookVisible ? '' : null">
  @if (course && module) {
    <div class="content-contain mx-auto my-4">
      <div class="flex flex-col items-start gap-2 mb-2">
        <prism-btn
          btnClass="btn-white !w-auto"
          (click)="goBackToList()"
          ariaLabel="Back to Modules">
          <i class="uil uil-arrow-left"></i>
          Back to Modules
        </prism-btn>
        <prism-title additionalClasses="text-3xl" [title]="course.name + ' — ' + module.name"></prism-title>
      </div>
      <hr class="h-1 bg-dark-purple border-0 my-2">

      <section [attr.aria-label]="module.name + ' content'">
        <div class="container mx-auto" style="height: 70vh;">
          <iframe
            id="module-iframe"
            [src]="iframeUrl"
            width="100%"
            height="100%"
            [title]="module.name + ' content'"
            (load)="onIframeLoad()"></iframe>
        </div>
      </section>

      <div class="mt-5 flex flex-wrap items-start justify-between gap-4">
        @if (requiresNotebookEntry) {
          <div class="flex items-center">
            <prism-btn
              ariaLabel="PRISM Digital Notebook Entry"
              (click)="openNotebook()">
              PRISM Digital Notebook Entry
            </prism-btn>
            <span class="ml-3 text-sm text-gray-600">
              {{ entries.length }} {{ entries.length === 1 ? 'entry' : 'entries' }} saved
            </span>
          </div>
        }

        @if (!alreadyCompleted && hasMinimumStay) {
          <prism-stay-timer
            subject="module"
            [durationSeconds]="moduleTimeoutSeconds"
            [startTime]="moduleStartTime">
          </prism-stay-timer>
        }
      </div>

      @if (!alreadyCompleted) {
        @if (requiresAcknowledgement) {
          <div class="flex flex-nowrap justify-center items-center my-5" [formGroup]="completeForm">
            <label for="module-acknowledge" class="cursor-pointer border-2 border-dark-purple p-2 mt-5 block">
              <input
                type="checkbox"
                id="module-acknowledge"
                aria-required="true"
                [checked]="isCompletedControl?.value"
                (change)="handleCheckbox()" />
              By submitting your reflections, you acknowledge that they are intended solely for your professional growth and
              development. Your responses will remain private.
              @if (requiresNotebookEntry) {
                <br><br>
                <span>
                  <span class="red-asterisk">*</span>
                  You must add at least one PRISM Digital Notebook Entry to enable the Agree and Submit button and checkbox.
                </span>
              }
            </label>
          </div>
        }

        <div class="flex flex-nowrap justify-center items-center mb-5">
          <prism-btn
            [disabled]="!canSubmit"
            btnClass="btn-purple flex-fit pt-2 w-48 text-nowrap mb-5"
            (keydown)="handleKeydown($event)"
            (click)="submit()">
            {{ submitButtonText }}
          </prism-btn>
        </div>
      } @else {
        <div class="flex justify-center items-center my-6">
          <p class="text-green-700 font-semibold text-lg">
            <i class="uil uil-check-circle"></i>
            This module has been completed and saved.
          </p>
        </div>
      }
    </div>
  } @else {
    <div class="content-contain mx-auto my-4">
      <p>Module not found.</p>
    </div>
  }
</main>

<!-- PRISM Digital Notebook modal -->
@if (isNotebookVisible && module) {
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
    role="presentation">
    <section
      #notebookDialog
      class="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] mx-4 overflow-y-auto outline-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="module-notebook-title"
      tabindex="-1"
      (keydown.escape)="closeNotebook()">

      <div class="flex justify-between items-center border-b px-6 py-4">
        <h2 id="module-notebook-title" class="text-lg font-semibold text-gray-800">
          {{ module.name }} — PRISM Digital Notebook
        </h2>
        <button (click)="closeNotebook()" aria-label="Close modal"
          class="text-gray-500 hover:text-gray-700 focus:outline-none">
          <i class="uil uil-times text-xl"></i>
        </button>
      </div>

      <div class="px-6 py-4 space-y-4">
        @if (hasNotebookQuestions) {
          <div class="bg-gray-50 border-l-4 border-dark-purple rounded-r-lg p-4">
            <h3 class="text-gray-800 text-base font-semibold mb-1">Actionable Reflection</h3>
            <div class="text-gray-700 module-notebook-prompt" [innerHTML]="notebookDescription"></div>
          </div>
        }

        <div class="flex justify-between items-center">
          <h3 class="text-gray-700 text-base font-medium">Entries</h3>
          <span class="text-sm text-gray-600">{{ entries.length }} saved</span>
        </div>

        <div class="space-y-3">
          @if (entries.length === 0) {
            <p class="text-gray-600 italic">No entries yet. Click "Add PRISM Digital Notebook Entry" to add one.</p>
          }
          @for (entry of entries; track entry.id) {
            <div class="bg-gray-50 rounded-lg p-4">
              <div class="flex justify-between items-start mb-2">
                <span class="text-sm font-semibold text-gray-500">
                  {{ entry.dateSubmitted | date : 'longDate' }}
                </span>
                @if (!alreadyCompleted) {
                  <prism-btn
                    variant="text"
                    [ariaLabel]="'Edit PRISM Digital Notebook entry from ' + (entry.dateSubmitted | date : 'longDate')"
                    (click)="editEntry($event, entry)">
                    <i class="uil uil-edit" aria-hidden="true"></i>
                  </prism-btn>
                }
              </div>
              <p class="text-gray-600 break-words whitespace-pre-wrap">{{ entry.journalEntry }}</p>
            </div>
          }
        </div>

        @if (showEntryForm) {
          <form
            [formGroup]="journalForm"
            (ngSubmit)="submitEntry()"
            class="space-y-4 border-t pt-4">
            <prism-input
              [control]="entry"
              [label]="editingEntryId ? 'Edit PRISM Digital Notebook Entry' : 'PRISM Digital Notebook Entry'"
              placeHolder="Enter PRISM Digital Notebook entry"
              [inputType]="InputType.Textarea"
              [minLength]="module.minEntryLimit"
              [maxLength]="2000"
              autocomplete="off">
            </prism-input>


            <div class="flex justify-end gap-2">
              @if (editingEntryId) {
                <prism-btn variant="text" btnClass="btn-danger" btnType="button" (click)="deleteEntry($event)">
                  Delete
                </prism-btn>
              }
              <prism-btn variant="text" btnType="button" (click)="cancelEntry($event)">Cancel</prism-btn>
              <prism-btn variant="primary" [disabled]="!journalForm.valid">
                {{ editingEntryId ? 'Save Edit' : 'Save Entry' }}
              </prism-btn>
            </div>
          </form>
        } @else if (!alreadyCompleted) {
          <prism-btn variant="outline" class="w-full mt-2" (click)="showAddEntryForm()">
            <i nz-icon nzType="plus"></i>
            Add PRISM Digital Notebook Entry
          </prism-btn>
        }
      </div>
    </section>
  </div>
}
